import React, { useState } from "react";
import { messagesAppendMessage } from "../../gen";
import type { ThreadResponse, MessageResponse } from "../../gen/models";

interface UseThreadMessengerOptions {
  thread: ThreadResponse | null;
  messages: MessageResponse[];
  setMessages: React.Dispatch<React.SetStateAction<MessageResponse[]>>;
  sortMessagesChronologically: (msgs: MessageResponse[]) => MessageResponse[];
  loadThreadData: (isBackground?: boolean) => Promise<void>;
  showToast: (message: string, options?: { type?: "info" | "success" | "warning" | "error" }) => void;
}

export function useThreadMessenger({
  thread,
  messages,
  setMessages,
  sortMessagesChronologically,
  loadThreadData,
  showToast,
}: UseThreadMessengerOptions) {
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || sending || !thread) return;

    const userContent = inputText.trim();
    setInputText("");
    setSending(true);

    const now = new Date().toISOString();
    const tempId = `temp-${crypto.randomUUID()}`;
    const channelTs = String(Date.now() / 1000);
    const optimisticUserMsg: MessageResponse = {
      id: tempId,
      org_id: thread.org_id,
      thread_id: thread.id,
      sequence_num: messages.length + 1,
      sender_role: "user",
      sender_name: "Patron",
      sender_id: thread.initiator_user_id || undefined,
      channel_message_ts: channelTs,
      content: userContent,
      metadata: { source: "knowledge-web-ui" },
      created_at: now,
    };

    setMessages((prev) => [...prev, optimisticUserMsg]);
    setIsWaitingForAgent(true);

    try {
      const appendRes = await messagesAppendMessage(thread.id, {
        sender_role: "user",
        sender_name: "Patron",
        sender_id: thread.initiator_user_id || undefined,
        channel_message_ts: channelTs,
        content: userContent,
        metadata: { source: "knowledge-web-ui" },
      });

      const appendedData = (appendRes as any).data;
      if (appendedData?.agent_turn) {
        setIsWaitingForAgent(false);
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== tempId);
          return sortMessagesChronologically([
            ...filtered,
            appendedData.user_turn,
            appendedData.agent_turn,
          ]);
        });
        return;
      }

      if (appendedData?.id) {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? appendedData : m))
        );
      }

      await loadThreadData(true);
    } catch (err: any) {
      showToast("Failed to dispatch message: " + (err?.message || "Unknown error"), { type: "error" });
      setIsWaitingForAgent(false);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  return {
    inputText,
    setInputText,
    sending,
    isWaitingForAgent,
    setIsWaitingForAgent,
    handleSendMessage,
  };
}
